import { useCallback, useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import publicTripService from '../../services/public-trip.service';
import { usePublicTrips } from '../contexts/public-trips-context';
import { useSorting } from '../contexts/sorting-context';
import { PublicTripCard } from './public-trip-card';

interface PublicTripCardListProps {
  direction?: string;
  searchQuery?: string;
}

function PublicTripCardList({
  direction,
  searchQuery
}: PublicTripCardListProps) {
  const {
    publicTrips,
    setPublicTrips,
    refreshPublicTrips,
    setRefreshPublicTrips
  } = usePublicTrips();
  const { sort } = useSorting();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [isLoading, setIsLoading] = useState(false);

  // Fetch public trips on mount and when refreshPublicTrips or sort changes
  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      let response;

      // If search query exists, use search endpoint
      if (searchQuery && searchQuery.trim().length > 0) {
        console.log('🔍 Searching public trips with query:', searchQuery);
        response = await publicTripService.searchPublicTrips(
          searchQuery,
          currentPage,
          20
        );
      } else {
        // Use optimized summary endpoint for better performance
        response = await publicTripService.getPublicTripsSummary(
          currentPage,
          20
        );
      }

      if (!response || !response.trips || response.trips.length === 0) {
        setPublicTrips([]);
        return;
      }

      setPublicTrips([...response.trips]);
    } catch (error) {
      console.error('❌ Error fetching public trips:', error);
      setPublicTrips([]);
    } finally {
      setIsLoading(false);
    }
  }, [setPublicTrips, currentPage, searchQuery]);

  useEffect(() => {
    getData();
    // Only setRefreshPublicTrips(false) if refreshPublicTrips was true
    if (refreshPublicTrips) {
      setRefreshPublicTrips(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, refreshPublicTrips, sort]);

  // Calculate the trips to be displayed on the current page
  const indexOfLastTrip = currentPage * itemsPerPage;
  const indexOfFirstTrip = indexOfLastTrip - itemsPerPage;
  const currentTrips = publicTrips.slice(indexOfFirstTrip, indexOfLastTrip);

  // Calculate the total number of pages
  const totalPages = Math.ceil(publicTrips.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) {
    return (
      <div className='text-center py-5'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (publicTrips.length === 0) {
    return (
      <div className='text-center py-5'>
        <p className='text-muted'>No public trips available</p>
      </div>
    );
  }

  return (
    <>
      <Row className='g-4'>
        {currentTrips.map((publicTrip) => {
          return (
            <Col
              key={publicTrip.id}
              xs={12}
              md={6}
              lg={4}
            >
              <PublicTripCard
                publicTrip={publicTrip}
                onClick={() => {
                  // Navigate to public trip view
                  console.log('View public trip:', publicTrip.id);
                }}
                onLikeToggle={(publicTripId, isLiked) => {
                  // Optional: Handle like state change for analytics or other purposes
                }}
                onShare={() => {
                  // Handle share — implement share modal here
                }}
              />
            </Col>
          );
        })}
      </Row>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='d-flex justify-content-center mt-4'>
          <nav>
            <ul className='pagination'>
              <li
                className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
              >
                <button
                  className='page-link'
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li
                  key={index}
                  className={`page-item ${
                    currentPage === index + 1 ? 'active' : ''
                  }`}
                >
                  <button
                    className='page-link'
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? 'disabled' : ''
                }`}
              >
                <button
                  className='page-link'
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </>
  );
}

export default PublicTripCardList;
